import {
  Avatar,
  Box,
  Flex,
  FormControl,
  GridItem,
  Portal,
  Select,
  Input,
  Radio,
  RadioGroup,
  SimpleGrid,
  FormErrorMessage,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PenIcon } from "../../../assets/icons/penicon";
import EmailVerificationModal from "@root/components/emailverification/emailverificationmodal";
import EmailOtpModal from "@root/components/emailverification/emailoptModal";
import EmailUpdatedModal from "@root/components/emailverification/emailupdatedModal";
import {
  MyProfileHeading,
  MyProfileSubtext,
  ProfileLabel,
  PasswordChangeLink,
  ProfileWrapper,
  AvatarEditWrapper,
  EditIconWrapper,
} from "@root/components/styles";
import ChangePasswordModal from "@root/components/change-password/changepassword";
import { useGlobalContext } from "@root/context/global-context";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useSelector } from "react-redux";
import { CustomButton } from "@root/components/common/button/customButton";
import { ProfileChangeHistoryDrawer } from "@root/components/common/drawer/ProfileChangeHistoryDrawer";
import { ChangeHistoryIcon } from "@root/assets/icons/historyIcon";
import {
  ORG_TYPE,
  PAGINATION_DEFAULTS,
  ROUTE_NAMES,
  SUBSCRIBER_TYPE,
  USER_TYPES,
} from "@root/constants";
import { useJournalistContext } from "@root/context/journalist-context";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { RoleChangeModal } from "@root/pages/auth/awaitingApproval/RoleChangeModal";
import CustomSelect from "@root/components/common/CustomSelect";
import useMediaQ from "@root/hooks/useMediaQ";
import { useNotification } from "@root/hooks/useNotification";
import { useNavigate, useLocation } from "react-router-dom";
import { useJournalistUpdateProfileTourGuide } from "@root/hooks/useJournalistUpdateProfileTourGuide";
import { usePledgeUpdateProfileTourGuide } from "@root/hooks/usePledgeUpdateProfileTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

export const UpdateProfile = () => {
  const { t } = useTranslation("common");
  const { slug } = useOrganizationBranding();
  const { showNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { sm } = useMediaQ();
  const navigate = useNavigate();
  const isMobile = !sm;
  const [organizationType, setOrganizationType] = useState<string>(
    ORG_TYPE.SUBSCRIPTION.toString(),
  );

  const [isAwaitingApprovalOpen, setIsAwaitingApprovalOpen] = useState(false);

  // Add state to track if we should show modal after successful update
  const [shouldShowModalAfterUpdate, setShouldShowModalAfterUpdate] =
    useState(false);

  // Add state to track if email has been verified in current session
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  const { fetchDepartments, fetchMoreDepartments, departmentsData } = useJournalistContext();
  const user: any = useSelector(getLoggedInUser);

  const [touched, setTouched] = useState({
    username: false,
    name: false,
    email: false,
    phone: false,
    companyId: false,
    companyName: false,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isChangeHistoryOpen, setIsChangeHistoryOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const {
    organizationOptions,
    fetchOrganizations,
    updateProfile,
    updateProfileLoading,
    isEmailVerifyOpen,
    setIsEmailVerifyOpen,
    getUserData,
    userData,
    getChatHistory,
    userHistory,
    historyCount,
    historyPage,
  } = useGlobalContext();

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    if (userData) {
      const role =
        userData.organizationType === ORG_TYPE.NON_SUBSCRIPTION
          ? SUBSCRIBER_TYPE.NON_SUBSCRIBER
          : SUBSCRIBER_TYPE.SUBSCRIBER;
      fetchOrganizations({ role, ...(slug && { slug }) });

      // Fetch departments based on subscriberId for subscription types
      if (
        (userData.organizationType === ORG_TYPE.SUBSCRIPTION ||
          userData.organizationType === ORG_TYPE.NON_SUBSCRIPTION) &&
        userData?.subscriberId
      ) {
        fetchDepartments({ subscriberId: userData?.subscriberId });
      }
    }
  }, [userData]);

  useEffect(() => {
    if (userData) {
      setUsername(userData?.username || "");
      setName(userData?.fullName || "");
      setEmail(userData?.email || "");
      setPhone(userData?.phone || "");
      setIsEmailVerified(false); // Reset verification status when userData changes

      const orgType =
        userData?.organizationType?.toString() ||
        ORG_TYPE.SUBSCRIPTION.toString();
      setOrganizationType(orgType);

      // Set company ID from userData.subscriberId for both org types
      if (userData?.subscriberId) {
        setCompanyId(userData.subscriberId.toString());
      }
    }
  }, [userData]);

  useEffect(() => {
    if (companyId) {
      fetchDepartments({ subscriberId: companyId });
    } else if (
      organizationType === ORG_TYPE.NON_SUBSCRIPTION.toString() &&
      userData?.subscriberId
    ) {
      fetchDepartments({ subscriberId: userData?.subscriberId });
    }
  }, [companyId, organizationType, userData?.subscriberId]);

  useEffect(() => {
    if (userData?.departmentRole && departmentsData?.rows) {
      // Check if the selected organization type matches the current userData organization type
      const currentOrgType =
        userData?.organizationType?.toString() ||
        ORG_TYPE.SUBSCRIPTION.toString();
      const isMatchingCurrentOrgType = organizationType === currentOrgType;

      // Only populate department/role if the organization type matches the current user data
      if (isMatchingCurrentOrgType) {
        // First try to find the department in the fetched departments
        let dept = departmentsData.rows.find(
          (d: any) => d.id === userData?.departmentRole?.parent?.id,
        );

        // If not found in departmentsData, use the data directly from userData.departmentRole
        if (!dept && userData?.departmentRole?.parent) {
          // Set department and role directly from userData
          setDepartment(userData.departmentRole.parent.title);
          setRole(userData.departmentRole.title);
          // Note: We don't set selectedDepartment since it's not in the current departments list
          setSelectedDepartment(null);
        } else if (dept) {
          // If found in departmentsData, use it normally
          setSelectedDepartment(dept);
          setDepartment(dept.title);

          const roleObj = dept.children?.find(
            (r: any) => r.id === userData?.departmentRole?.id,
          );

          if (roleObj) {
            setRole(roleObj?.title);
          }
        }
      } else {
        // If org type doesn't match, reset department and role
        setDepartment("");
        setRole("");
        setSelectedDepartment(null);
      }
    } else {
      // Reset department and role if no department role exists
      setDepartment("");
      setRole("");
      setSelectedDepartment(null);
    }
  }, [
    departmentsData,
    userData?.departmentRole,
    organizationType,
    userData?.organizationType,
  ]);

  // Map subscriberId to company name from organizationOptions
  useEffect(() => {
    if (companyId && organizationOptions?.length > 0) {
      const matchedCompany = organizationOptions.find(
        (org: any) => org.id.toString() === companyId,
      );
      if (matchedCompany && matchedCompany.companyName) {
        setCompanyName(matchedCompany.companyName);
      }
    }
  }, [companyId, organizationOptions]);

  const renderCompanyOptions = () =>
    organizationOptions
      ?.filter((org: any) => org?.companyName)
      .map((org: any) => (
        <option key={org?.id} value={org?.id}>
          {org?.companyName}
        </option>
      ));

  const isValidEmail = email.trim()
    ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    : true;

  // Phone validation similar to signup: 8-12 digits after removing spaces
  const phoneError = phone.trim()
    ? (() => {
        const phoneDigits = phone.replace(/\s/g, "")?.length || 0;
        if (phoneDigits < 8 || phoneDigits > 12) {
          return t("signup.phoneDigitsError");
        }
        return "";
      })()
    : "";
  const isValidPhone = !phoneError;

  // Company name is required for subscription type journalists only
  const isCompanyNameRequired =
    user?.role === USER_TYPES.JOURNALIST &&
    organizationType === ORG_TYPE.SUBSCRIPTION.toString();
  const isCompanyNameValid = isCompanyNameRequired
    ? companyName.trim().length > 0
    : true;

  const isFormValid =
    (!username.trim() || username.trim()) &&
    (!name.trim() || name.trim()) &&
    (!email.trim() || (email.trim() && isValidEmail)) &&
    (!phone.trim() || (phone.trim() && isValidPhone)) &&
    (!companyId || companyId) &&
    isCompanyNameValid;

  // Check if any values have changed from userData
  const hasNameChanged =
    userData && name.trim() !== (userData?.fullName || "").trim();
  // Modified: Also check if email is already verified
  const hasEmailChanged =
    userData &&
    user?.role !== USER_TYPES.SUPER_ADMIN &&
    email.trim() !== (userData?.email || "").trim() &&
    !isEmailVerified; // Don't consider email as changed if already verified
  const hasPhoneChanged =
    userData && phone.trim() !== (userData?.phone || "").trim();
  const hasImageChanged = imageFile !== null;
  const hasCompanyChanged =
    userData && companyId !== (userData?.subscriberId?.toString() || "");
  const hasCompanyNameChanged =
    userData && companyName !== (userData?.companyName || "");

  // Check department change - compare against userData department title
  const hasDepartmentChanged = () => {
    // If user has no department initially but now has one selected
    if (!userData?.departmentRole && department.trim()) {
      return true;
    }

    // If user has department but no departmentsData loaded yet
    if (!departmentsData?.rows) return false;

    // If user had department but now cleared it
    if (userData?.departmentRole && !department.trim()) {
      return true;
    }

    // Compare current selection with user's existing department
    if (userData?.departmentRole) {
      const userDept = departmentsData.rows.find(
        (d: any) => d.id === userData?.departmentRole?.parent?.id,
      );
      return userDept && department !== userDept.title;
    }

    return false;
  };

  // Check role change - compare against userData role title
  const hasRoleChanged = () => {
    // If user has no role initially but now has one selected
    if (!userData?.departmentRole && role.trim()) {
      return true;
    }

    // If user has role but no departmentsData loaded yet
    if (!departmentsData?.rows) return false;

    // If user had role but now cleared it
    if (userData?.departmentRole && !role.trim()) {
      return true;
    }

    // Compare current selection with user's existing role
    if (userData?.departmentRole) {
      const userDept = departmentsData.rows.find(
        (d: any) => d.id === userData?.departmentRole?.parent?.id,
      );
      if (!userDept) return false;
      const userRole = userDept.children?.find(
        (r: any) => r.id === userData?.departmentRole?.id,
      );
      return userRole && role !== userRole.title;
    }

    return false;
  };

  // Check department role ID change
  const hasDepartmentRoleIdChanged = () => {
    // If both department and role are selected (new or changed)
    if (selectedDepartment && role && selectedDepartment.children) {
      const selectedRole = selectedDepartment.children?.find(
        (r: any) => r.title === role,
      );

      // If no initial departmentRole, this is a new selection
      if (!userData?.departmentRole?.id) {
        return !!selectedRole;
      }

      // If there was a departmentRole, check if it changed
      if (selectedRole) {
        return selectedRole.id !== userData.departmentRole.id;
      }
    }
    return false;
  };

  const hasOrganizationTypeChanged =
    userData &&
    organizationType !==
      (userData?.organizationType?.toString() ||
        ORG_TYPE.SUBSCRIPTION.toString());

  const hasAnyChanges =
    user?.role === USER_TYPES.JOURNALIST
      ? // For journalists, check all fields including company/department/role
        hasNameChanged ||
        hasEmailChanged ||
        hasPhoneChanged ||
        hasImageChanged ||
        hasCompanyChanged ||
        hasCompanyNameChanged ||
        hasDepartmentChanged() ||
        hasRoleChanged() ||
        hasDepartmentRoleIdChanged() ||
        hasOrganizationTypeChanged
      : // For non-journalists, only check basic profile fields
        hasNameChanged || hasEmailChanged || hasPhoneChanged || hasImageChanged;

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert(t("form.invalidImageType"));
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(t("form.imageTooLarge"));
        return;
      }

      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
    }
  };

  const performUpdate = async (
    shouldShowModal: boolean,
    companyNameForNotification?: string,
  ) => {
    if (imageFile) {
      const formData = new FormData();
      // Only send fields that have changed
      if (hasNameChanged && name) {
        formData.append("fullName", name);
      }
      // Don't send email for super-admin, and only if changed
      if (user?.role !== USER_TYPES.SUPER_ADMIN && hasEmailChanged && email) {
        formData.append("email", email);
      }
      if (hasPhoneChanged && phone) {
        formData.append("phone", phone);
      }
      // Always send image file if it exists (it's a new file)
      if (user?.role === USER_TYPES.SUPER_ADMIN) {
        formData.append("file", imageFile);
      } else {
        formData.append("avatar", imageFile);
      }

      // Don't send organizationType for super-admin, and only if changed
      if (user?.role !== USER_TYPES.SUPER_ADMIN && hasOrganizationTypeChanged) {
        formData?.append("organizationType", organizationType);
      }

      if (hasCompanyNameChanged && companyName) {
        formData?.append("companyName", companyName);
      }

      if (
        hasCompanyChanged &&
        companyId &&
        !isNaN(Number(companyId)) &&
        Number.isInteger(Number(companyId))
      ) {
        formData?.append("subscriberId", String(Number(companyId)));
      }

      // Handle department/role changes
      if (
        hasDepartmentChanged() ||
        hasRoleChanged() ||
        hasDepartmentRoleIdChanged()
      ) {
        // If department changed, send the department ID
        if (hasDepartmentChanged() && selectedDepartment?.id) {
          formData?.append("departmentId", selectedDepartment.id.toString());
        }

        // If both department and role are selected, send the departmentRoleId
        if (selectedDepartment && role) {
          const selectedRole = selectedDepartment?.children?.find(
            (r: any) => r.title === role,
          );
          if (selectedRole && selectedRole.id) {
            formData?.append("departmentRoleId", selectedRole.id.toString());
          }
        } else if (hasRoleChanged()) {
          // If role changed but not selected, clear it
          formData?.append("departmentRoleId", "null");
        }
      }

      formData?.append("language", "ko");
      const result = await updateProfile(user?.id, formData);

      // Show notification if company was changed and update was successful
      if (result && companyNameForNotification) {
        showNotification({
          type: "warning",
          message: t("notifications.waitingForApproval", {
            companyName: companyNameForNotification,
          }),
        });
      }
    } else {
      const payload: any = {};
      // Only send fields that have changed
      if (hasNameChanged && name) {
        payload.fullName = name;
      }
      // Don't send email for super-admin, and only if changed
      if (user?.role !== USER_TYPES.SUPER_ADMIN && hasEmailChanged && email) {
        payload.email = email;
      }
      if (hasPhoneChanged && phone) {
        payload.phone = phone;
      }

      // Don't send organizationType for super-admin, and only if changed
      if (user?.role !== USER_TYPES.SUPER_ADMIN && hasOrganizationTypeChanged) {
        payload.organizationType = Number(organizationType);
      }

      if (hasCompanyNameChanged && companyName) {
        payload.companyName = companyName;
      }

      if (
        hasCompanyChanged &&
        companyId &&
        !isNaN(Number(companyId)) &&
        Number.isInteger(Number(companyId))
      ) {
        payload.subscriberId = Number(companyId);
      }

      // Handle department/role changes
      if (
        hasDepartmentChanged() ||
        hasRoleChanged() ||
        hasDepartmentRoleIdChanged()
      ) {
        // If department changed, send the department ID
        if (hasDepartmentChanged() && selectedDepartment?.id) {
          payload.departmentId = selectedDepartment.id;
        }

        // If both department and role are selected, send the departmentRoleId
        if (selectedDepartment && role) {
          const selectedRole = selectedDepartment?.children?.find(
            (r: any) => r.title === role,
          );
          if (selectedRole) {
            payload.departmentRoleId = selectedRole?.id;
          }
        } else if (hasRoleChanged()) {
          // If role changed but not selected, clear it
          payload.departmentRoleId = null;
        }
      }

      const result = await updateProfile(user?.id, payload);

      if (
        result &&
        hasEmailChanged &&
        userData?.role !== USER_TYPES.SUPER_ADMIN &&
        (userData?.role == USER_TYPES.SUBSCRIBER ||
          userData?.role == USER_TYPES.SUBSCRIBER ||
          userData?.role == USER_TYPES.ADMIN)
      ) {
        window.location.reload();
        return;
      }

      // Show notification if company was changed and update was successful
      if (result && companyNameForNotification) {
        showNotification({
          type: "warning",
          message: t("notifications.waitingForApproval", {
            companyName: companyNameForNotification,
          }),
        });
      }
    }

    setShouldShowModalAfterUpdate(shouldShowModal);
  };

  const onSubmit = () => {
    // Validate company name is required for subscriber journalists only
    if (
      user?.role === USER_TYPES.JOURNALIST &&
      organizationType === ORG_TYPE.SUBSCRIPTION.toString() &&
      !companyName.trim()
    ) {
      showNotification({
        type: "error",
        message: t("form.companyNameRequired"),
      });
      return;
    }

    const isOrganizationChanging =
      userData && companyId !== (userData?.subscriberId?.toString() || "");

    // Check if company name actually changed
    const isCompanyNameChanging =
      userData && companyName !== (userData?.companyName || "");

    const isTypeChangingFromNonSubscriberToSubscriber =
      userData &&
      (userData?.organizationType?.toString() ||
        ORG_TYPE.SUBSCRIPTION.toString()) ===
        ORG_TYPE.NON_SUBSCRIPTION.toString() &&
      organizationType === ORG_TYPE.SUBSCRIPTION.toString();

    const isSubscriberChangingDetails =
      userData &&
      (userData?.organizationType?.toString() ||
        ORG_TYPE.SUBSCRIPTION.toString()) ===
        ORG_TYPE.SUBSCRIPTION.toString() &&
      (isOrganizationChanging ||
        hasDepartmentChanged() ||
        hasRoleChanged() ||
        hasDepartmentRoleIdChanged());

    // Show notification with company name when:
    // 1. Changing from non-subscriber to subscriber with company change
    // 2. Subscriber changing company (organization changing)
    const shouldShowCompanyNotification =
      (isTypeChangingFromNonSubscriberToSubscriber || isOrganizationChanging) &&
      companyName.trim();

    const newCompanyName = shouldShowCompanyNotification
      ? companyName
      : undefined;

    // Show await approval modal when changing from non-subscriber to subscriber
    if (isTypeChangingFromNonSubscriberToSubscriber) {
      setIsAwaitingApprovalOpen(true);
      performUpdate(true, newCompanyName);
      return;
    } else if (isSubscriberChangingDetails) {
      // Show notification for subscriber changing details
      performUpdate(true, newCompanyName);
    } else {
      performUpdate(false);
    }
  };

  useEffect(() => {
    return () => {
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
      }
    };
  }, [selectedImage]);

  const handleOrganizationTypeChange = (value: string) => {
    setOrganizationType(value);
    fetchOrganizations({
      role:
        value === ORG_TYPE.SUBSCRIPTION.toString()
          ? SUBSCRIBER_TYPE.SUBSCRIBER
          : SUBSCRIBER_TYPE.NON_SUBSCRIBER,
      ...(slug && { slug }),
    });

    // Reset fields first
    setCompanyId("");
    setCompanyName("");
    setDepartment("");
    setRole("");
    setSelectedDepartment(null);

    // Check if the selected organization type matches the current userData organization type
    const currentOrgType =
      userData?.organizationType?.toString() ||
      ORG_TYPE.SUBSCRIPTION.toString();
    const isMatchingCurrentOrgType = value === currentOrgType;

    // Only populate fields if the organization type matches the current user data
    if (isMatchingCurrentOrgType) {
      // Use userData for both subscription and non-subscription
      if (userData?.subscriberId) {
        setCompanyId(userData.subscriberId.toString());
        // Fetch departments for the subscriber
        fetchDepartments({ subscriberId: userData.subscriberId });
      }
    }
  };

  const isLoggedInUser: any = useSelector(getLoggedInUser);
  const { pathname } = useLocation();
  const { journalistUpdateProfileTourGuide, isMobile: isMobileJournalist } =
    useJournalistUpdateProfileTourGuide();
  const { pledgeUpdateProfileTourGuide, isMobile: isMobilePledge } =
    usePledgeUpdateProfileTourGuide();
  const isOnPledgeUpdateProfile =
    pathname === ROUTE_NAMES.PLEDGE_UPDATE_PROFILE;
  const updateProfileTourGuide = isOnPledgeUpdateProfile
    ? pledgeUpdateProfileTourGuide
    : journalistUpdateProfileTourGuide;
  const isMobileTour = isOnPledgeUpdateProfile
    ? isMobilePledge
    : isMobileJournalist;

  const handleChangeHistoryClick = async (id: any) => {
    setSelectedUserId(id); // Store the ID
    const payload = {
      id: id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (historyPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    };
    try {
      await getChatHistory(payload);
      setIsChangeHistoryOpen(true);
    } catch (error) {
      // Error fetching user history
    }
  };

  const oClose = async () => {
    try {
      await getUserData();
    } catch (error) {
      // Error fetching user data
    }
  };

  return (
    <>
      {userData?.role == USER_TYPES.JOURNALIST ? (
        <>
          <EmailVerificationModal
            isOpen={isEmailVerifyOpen}
            onClose={() => setIsEmailVerifyOpen(false)}
            onVerify={() => {
              setIsEmailVerifyOpen(false);
              setIsOtpModalOpen(true);
            }}
          />
          <EmailOtpModal
            isOpen={isOtpModalOpen}
            onClose={() => setIsOtpModalOpen(false)}
            email={email}
            onVerify={({ otp, email }) => {
              setIsOtpModalOpen(false);
              setIsSuccessModalOpen(true);
              setIsEmailVerified(true); // Mark email as verified
            }}
          />
          <EmailUpdatedModal isOpen={isSuccessModalOpen} onClose={oClose} />
        </>
      ) : null}

      <ChangePasswordModal isOpen={isOpen} onClose={onClose} />

      <ProfileWrapper>
        <Box w="100%" maxH={"calc(100vh - 20px)"} overflowY={"auto"}>
          <Flex justifyContent={"space-between"}>
            <Box>
              <MyProfileHeading>{t("userProfile.myProfile")}</MyProfileHeading>
              <MyProfileSubtext>
                {t("userProfile.checkYourProfile")}
              </MyProfileSubtext>
            </Box>
            {isLoggedInUser?.role !== USER_TYPES.SUPER_ADMIN && (
              <Box data-tour-item="journalist-update-profile-change-history">
                <CustomButton
                  leftIcon={<ChangeHistoryIcon />}
                  text={t("userProfile.changeHistory")}
                  onClick={() => handleChangeHistoryClick(user?.id)}
                  isLoading={false}
                  isDisabled={false}
                  border={"1px solid #1819B4"}
                  borderRadius="8px"
                  bgColor={"transparent"}
                  color={"#1819B4"}
                  fontSize={"14px"}
                  fontWeight={500}
                  padding={"10px 20px"}
                />
              </Box>
            )}
          </Flex>
          <Flex direction="column" align="center" justify="center" mb={8}>
            <AvatarEditWrapper data-tour-item="journalist-update-profile-avatar">
              <Avatar
                name={userData?.fullName}
                src={selectedImage || userData?.avatar}
                boxSize={{ base: "80px", md: "120px" }}
                mb={2}
              />
              <EditIconWrapper>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
                <PenIcon onClick={handleImageClick} cursor="pointer" />
              </EditIconWrapper>
            </AvatarEditWrapper>
          </Flex>

          <Flex justify="center">
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={6}
              maxW="840px"
              w="100%"
            >
              <Box data-tour-item="journalist-update-profile-name">
                <FormControl isInvalid={touched.name && !name.trim()}>
                  <ProfileLabel>{t("userProfile.name")}</ProfileLabel>
                  <Input
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, name: true }))
                    }
                  />
                </FormControl>
              </Box>

              <Box data-tour-item="journalist-update-profile-email">
                <FormControl isInvalid={touched.email && !isValidEmail}>
                  <ProfileLabel>{t("userProfile.email")}</ProfileLabel>
                  <Input
                    placeholder="test@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setIsEmailVerified(false); // Reset verification when email changes
                    }}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, email: true }))
                    }
                  />
                  {touched.email && !isValidEmail && (
                    <FormErrorMessage>
                      {t("form.invalidEmail")}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </Box>

              {/* Phone */}
              <Box data-tour-item="journalist-update-profile-phone">
                <FormControl isInvalid={touched.phone && !isValidPhone}>
                  <ProfileLabel>{t("userProfile.phoneNumber")}</ProfileLabel>
                  <Input
                    placeholder="82 02 1234 5678"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, phone: true }))
                    }
                  />
                  {touched.phone && phoneError && (
                    <FormErrorMessage>{phoneError}</FormErrorMessage>
                  )}
                </FormControl>
              </Box>
              {isLoggedInUser?.role === USER_TYPES?.JOURNALIST && (
                <Box data-tour-item="journalist-update-profile-organization-type">
                  <FormControl>
                    <ProfileLabel>
                      {t("userProfile.organizationChoice")}
                    </ProfileLabel>
                    <RadioGroup
                      onChange={handleOrganizationTypeChange}
                      value={organizationType}
                      display="flex"
                      gap="20px"
                      mt={2}
                    >
                      <Radio value="1">{t("userProfile.subscriber")}</Radio>
                      <Radio value="2">{t("userProfile.nonSubscriber")}</Radio>
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}

              {isLoggedInUser?.role === USER_TYPES?.JOURNALIST && (
                <Box data-tour-item="journalist-update-profile-company">
                  <FormControl
                    isInvalid={
                      touched.companyName &&
                      organizationType === ORG_TYPE.SUBSCRIPTION.toString() &&
                      !companyName.trim()
                    }
                  >
                    <ProfileLabel>{t("userProfile.companyName")}</ProfileLabel>
                    <CustomSelect
                      placeholder={t("userProfile.selectCompany")}
                      value={companyId}
                      onChange={(selectedId: string) => {
                        setCompanyId(selectedId);
                        const selectedOrg = organizationOptions?.find(
                          (org: any) => org?.id === selectedId,
                        );
                        if (selectedOrg) {
                          setCompanyName(selectedOrg.companyName);
                          fetchDepartments({ subscriberId: selectedOrg.id });
                          // Reset department and role when company changes
                          setDepartment("");
                          setRole("");
                          setSelectedDepartment(null);
                        } else {
                          setCompanyName("");
                          setDepartment("");
                          setRole("");
                          setSelectedDepartment(null);
                        }
                      }}
                      onBlur={() =>
                        setTouched((prev) => ({ ...prev, companyName: true }))
                      }
                      isSearchable={true}
                      onSearch={(term) =>
                        fetchOrganizations({
                          role:
                            organizationType === ORG_TYPE.SUBSCRIPTION.toString()
                              ? SUBSCRIBER_TYPE.SUBSCRIBER
                              : SUBSCRIBER_TYPE.NON_SUBSCRIBER,
                          search: term,
                          ...(slug && { slug }),
                        })
                      }
                      options={organizationOptions
                        ?.filter((org: any) => org?.companyName)
                        .map((org: any) => ({
                          value: org.id,
                          label: org.companyName,
                        }))}
                      borderRadius="8px"
                      width={"400px"}
                      height={"52px"}
                    />
                    {touched.companyName &&
                      user?.role === USER_TYPES.JOURNALIST &&
                      organizationType === ORG_TYPE.SUBSCRIPTION.toString() &&
                      !companyName.trim() && (
                        <FormErrorMessage>
                          {t("form.companyNameRequired") ||
                            "Company name is required"}
                        </FormErrorMessage>
                      )}
                  </FormControl>
                </Box>
              )}
              {isLoggedInUser?.role === USER_TYPES?.JOURNALIST && (
                <Box data-tour-item="journalist-update-profile-department">
                  <FormControl>
                    <ProfileLabel>{t("signup.department")}</ProfileLabel>
                    <CustomSelect
                      placeholder={t("signup.departmentPlaceholder")}
                      value={department}
                      borderRadius="8px"
                      width={"400px"}
                      height={"52px"}
                      onChange={(value: string) => {
                        setDepartment(value);
                        setRole("");
                        setSelectedDepartment(null);
                        const dept = departmentsData?.rows?.find(
                          (d: any) => d.title === value,
                        );
                        if (dept) {
                          setSelectedDepartment(dept);
                        }
                      }}
                      isDisabled={!departmentsData?.rows?.length || !companyId}
                      isSearchable={true}
                      onSearch={(term) => {
                        if (companyId) {
                          fetchDepartments({ subscriberId: companyId, search: term });
                        }
                      }}
                      onScrollEnd={() => fetchMoreDepartments()}
                      options={
                        departmentsData?.rows?.map((dept: any) => ({
                          value: dept.title,
                          label: dept.title,
                        })) || []
                      }
                    />
                  </FormControl>
                </Box>
              )}
              {isLoggedInUser?.role === USER_TYPES?.JOURNALIST && (
                <Box data-tour-item="journalist-update-profile-role">
                  <FormControl>
                    <ProfileLabel>{t("signup.role")}</ProfileLabel>
                    <CustomSelect
                      placeholder={t("signup.rolePlaceholder")}
                      value={role}
                      borderRadius="8px"
                      width={"400px"}
                      height={"52px"}
                      onChange={(value: string) => setRole(value)}
                      isDisabled={!selectedDepartment || !department}
                      options={
                        selectedDepartment?.children?.map((r: any) => ({
                          value: r.title,
                          label: r.title,
                        })) || []
                      }
                    />
                  </FormControl>
                </Box>
              )}

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Box
                  mt={2}
                  data-tour-item="journalist-update-profile-change-password"
                >
                  <PasswordChangeLink onClick={onOpen}>
                    {t("userProfile.update/changePassword")}
                  </PasswordChangeLink>
                </Box>
              </GridItem>
            </SimpleGrid>
          </Flex>
        </Box>
        <Box
          w={isMobile ? "90%" : ""}
          position="fixed"
          bottom="24px"
          right="24px"
          zIndex={10}
          data-tour-item="journalist-update-profile-save"
        >
          <Button
            w="100%"
            type="submit"
            size="lg"
            alignSelf="flex-end"
            bgColor={"#1819B4"}
            fontSize={"14px"}
            fontWeight={500}
            isDisabled={!isFormValid || !hasAnyChanges}
            onClick={onSubmit}
            isLoading={updateProfileLoading}
          >
            {t("userProfile.saveChanges")}
          </Button>
        </Box>
        <ProfileChangeHistoryDrawer
          isOpen={isChangeHistoryOpen}
          onClose={() => setIsChangeHistoryOpen(false)}
          viewHistoryChange={handleChangeHistoryClick}
          userId={selectedUserId}
        />
        <CustomPopup
          bg={"transparent"}
          h={"400px"}
          width="520px"
          open={isAwaitingApprovalOpen}
          onClose={() => setIsAwaitingApprovalOpen(false)}
          title={t("awaitingApproval.title")}
          children={
            <RoleChangeModal onClose={() => setIsAwaitingApprovalOpen(false)} />
          }
        />

        {!isMobileTour && !updateProfileTourGuide.isActive && (
          <Portal>
            <RestartTourButton
              onClick={updateProfileTourGuide.startTour}
              position={{ bottom: "64px", right: "24px" }}
            />
          </Portal>
        )}

        {!isMobileTour &&
          updateProfileTourGuide.isActive &&
          updateProfileTourGuide.currentStepData && (
            <TourGuide
              isActive={updateProfileTourGuide.isActive}
              currentStep={updateProfileTourGuide.currentStepData}
              currentStepIndex={updateProfileTourGuide.currentStep}
              totalSteps={updateProfileTourGuide.totalSteps}
              onNext={updateProfileTourGuide.nextStep}
              onBack={updateProfileTourGuide.previousStep}
              onClose={updateProfileTourGuide.stopTour}
              targetSelector={
                updateProfileTourGuide.currentStepData.targetSelector
              }
              borderRadius="8px"
            />
          )}
      </ProfileWrapper>
    </>
  );
};
